import gql from 'graphql-tag';

import { mapPersonaForBackendInput } from 'src/data/persona';
import { getSingleProp } from 'src/utils/lib/graphql';
import apolloClient from 'src/store/client';
import { BRIEF_DEBUG_DATA_VISIBLE } from 'src/lib/featureFlags';
import { FRAGMENT_SUGGEST_REQUEST } from './schema';

const MUTATION = gql`
  mutation SuggestDocumentVariations(
    $projectId: ID!
    $generationTool: GenerationToolEnum!
    $subGenerationToolsProps: JSON
    $brief: BriefInput!
    $inputText: String
    $scoringMethod: ScoringMethodInput
    $suggestionMode: String!
    $language: String!
    $languageTargetFormality: LanguageTargetFormalityEnum
    $numberOfVariations: Int!
    $tovInput: TovInput
    $customFormulaId: ID
    $debugAcPrompt: String
    $freestyleToolId: ID
    $freestyleToolAllInstructions: String
    $freestyleToolAllInstructionsData: JSON
    $customerPersonaId: ID
    $customerPersona: CustomerPersonaInput
    $annotations: [SuggestionAnnotationInput!]
    $sourceType: String
    $url: String
    $manualInstructions: String
    $avoidWords: [String!]
    $onlyImproveScore: Boolean
    $originalVariationId: ID
    $extractedBrief: String
    $urlExtractions: [UrlExtractionInput!]
    $callToAction: String
    $maxChars: Int
    $generationCustomModelId: ID
    $promptImageId: ID
  ) {
    suggestVariationsForDocumentEditor(
      projectId: $projectId
      generationTool: $generationTool
      subGenerationToolsProps: $subGenerationToolsProps
      brief: $brief
      inputText: $inputText
      scoringMethod: $scoringMethod
      suggestionMode: $suggestionMode
      language: $language
      languageTargetFormality: $languageTargetFormality
      numberOfVariations: $numberOfVariations
      tovInput: $tovInput
      customFormulaId: $customFormulaId
      debugAcPrompt: $debugAcPrompt
      freestyleToolId: $freestyleToolId
      freestyleToolAllInstructions: $freestyleToolAllInstructions
      freestyleToolAllInstructionsData: $freestyleToolAllInstructionsData
      customerPersonaId: $customerPersonaId
      customerPersona: $customerPersona
      annotations: $annotations
      sourceType: $sourceType
      url: $url
      manualInstructions: $manualInstructions
      avoidWords: $avoidWords
      onlyImproveScore: $onlyImproveScore
      originalVariationId: $originalVariationId
      extractedBrief: $extractedBrief
      urlExtractions: $urlExtractions
      callToAction: $callToAction
      maxChars: $maxChars
      generationCustomModelId: $generationCustomModelId
      promptImageId: $promptImageId
    ) {
      ...SuggestionRequest
    }
  }
  ${FRAGMENT_SUGGEST_REQUEST}
`;

const prepareContext = (projectId, generationTool, params) => {
  if (!BRIEF_DEBUG_DATA_VISIBLE) {
    return null;
  }

  const title = `"document suggest" request [projectId: ${projectId}, generationTool: ${generationTool}, params: ${JSON.stringify(
    params
  )}]`;
  return { traceAuthorComplete: true, traceAuthorCompleteTitle: title };
};

export const suggestDocumentVariations = async ({
  projectId,
  generationTool,
  subGenerationToolsProps,
  brief,
  inputText,
  scoringMethod,
  suggestionMode,
  language,
  numberOfVariations,
  tovInput,
  customFormulaId,
  debugAcPrompt,
  freestyleToolId,
  freestyleToolAllInstructions,
  freestyleToolAllInstructionsData,
  languageTargetFormality,
  customerPersona,
  annotations,
  sourceType,
  url,
  manualInstructions,
  avoidWords,
  onlyImproveScore,
  originalVariationId,
  extractedBrief,
  urlExtractions,
  callToAction,
  maxChars,
  generationCustomModelId,
  promptImageId,
}) => {
  const variables = {
    projectId,
    generationTool,
    subGenerationToolsProps: subGenerationToolsProps
      ? JSON.stringify(subGenerationToolsProps)
      : null,
    brief,
    inputText,
    scoringMethod,
    suggestionMode,
    language,
    numberOfVariations,
    tovInput,
    customFormulaId,
    debugAcPrompt,
    freestyleToolId,
    freestyleToolAllInstructions,
    freestyleToolAllInstructionsData: JSON.stringify(freestyleToolAllInstructionsData),
    languageTargetFormality,
    customerPersonaId: customerPersona?.id,
    customerPersona: mapPersonaForBackendInput(customerPersona),
    annotations,
    sourceType,
    url,
    manualInstructions,
    avoidWords,
    onlyImproveScore,
    originalVariationId,
    extractedBrief,
    urlExtractions,
    callToAction,
    maxChars,
    generationCustomModelId,
    promptImageId,
  };

  const response = await apolloClient.mutate({
    mutation: MUTATION,
    variables,
    context: prepareContext(projectId, generationTool, variables),
  });
  return getSingleProp(response.data);
};
